<template>
  <div class="display" :class="{ kiosk }" @dblclick="toggleFullscreen">

    <!-- Shown briefly on load and whenever the mouse moves, then fades. A wall
         screen sees nothing; someone at a desk gets told how to do it. -->
    <transition name="hint-fade">
      <p v-if="showHint" class="hint">
        {{ isFullscreen ? 'Double-click to exit full screen' : 'Double-click for full screen' }}
      </p>
    </transition>

    <!-- Only in kiosk mode.
         In a window the app shell already puts a header above this one, with
         the same logo and wordmark in it, so this drew a second bar saying the
         same thing. The wall screen has no app shell, which is why it carries
         its own. -->
    <header v-if="kiosk" class="bar">
      <div class="brand">
        <img class="logo" src="@/assets/Epoka.png" alt="Epoka">
        <span class="divider"></span>
        <span class="product">Vero</span>
      </div>

      <div class="now">
        <!-- Local time where the people on screen actually are. A hallway
             screen in Denmark showing Pakistan should show Pakistan's clock,
             otherwise "in since 08:04" reads against the wrong day. -->
        <span class="clock">{{ clock }}</span>
        <span class="today">{{ todayLabel }}</span>
      </div>
    </header>

    <div v-if="loading" class="state">Reading the directory</div>

    <div v-else-if="error" class="state error">{{ error }}</div>

    <div v-else-if="slides.length === 0" class="state">Nobody to show</div>

    <template v-else>
      <div class="site-head">
        <span class="chip">{{ current.code }}</span>
        <h1 class="site-name">{{ current.name }}</h1>

        <!-- The clock moves here when the bar above is hidden. It is the
             country's local time, not the viewer's, so it is worth keeping even
             in a window: the browser's own clock answers a different
             question. -->
        <span v-if="!kiosk" class="inline-now">
          <span class="inline-clock">{{ clock }}</span>
          <span class="inline-today">{{ todayLabel }}</span>
        </span>
        <span v-if="current.pages > 1" class="page-of">
          {{ current.page }} / {{ current.pages }}
        </span>

        <!-- With cycling off the screen no longer moves on its own, so there
             has to be a way to reach the other countries. Next to the name they
             change, rather than tucked in a corner. -->
        <div v-if="showArrows" class="steps">
          <button class="step" aria-label="Previous country" @click="back">&#8592;</button>
          <button class="step" aria-label="Next country" @click="advance">&#8594;</button>
        </div>
      </div>

      <main ref="stage" class="stage">
      <div
        class="columns"
        :style="{ ...layoutVars, gridTemplateColumns: columnTemplate }"
      >
        <div v-for="(column, ci) in current.columns" :key="ci" class="column">
          <section v-for="dept in column" :key="dept.key" class="dept">
            <h2 class="dept-name">
              {{ dept.name }}<span v-if="dept.continued" class="cont">cont.</span>
            </h2>

            <ul class="people">
              <li
                v-for="person in dept.people"
                :key="person.id"
                class="person"
                :class="personClass(person)"
              >
                <!-- BADGING IN BEATS A STATUS THEY SET EARLIER.
                     A door is evidence; a status is an intention. Somebody who
                     said "out of office" on Monday and then badged into
                     Singapore on Tuesday is in Singapore, and the board should
                     say so: they are reachable, which is the opposite of what
                     the status implies. -->
                <span
                  v-if="person.checkedIn && person.badge"
                  class="badge"
                  :style="{ background: person.badgeColour }"
                >{{ person.badge }}</span>

                <!-- Not in a building, but they told us where they are. -->
                <span v-else-if="person.statusIcon" class="status-icon" :title="person.statusLabel">
                  {{ person.statusIcon }}
                </span>

                <!-- Neither: their office, greyed, if Entra records one. -->
                <span v-else-if="person.badge" class="badge">{{ person.badge }}</span>

                <!-- Nothing when Entra has no office. The gap keeps the name in
                     line with the rows that do have one. -->
                <span v-else class="badge-gap"></span>

                <span class="person-name">{{ person.name }}</span>
              </li>
            </ul>
          </section>
        </div>
        </div>
      </main>
    </template>

    <!-- News sits directly above the timer line, so it reads as part of the
         screen's furniture rather than interrupting the people. -->
    <div v-if="currentNews" class="news-strip">
      <span class="news-mark">News</span>
      <p class="news-body">{{ currentNews.body }}</p>
      <span v-if="news.length > 1" class="news-count">{{ newsIndex + 1 }}/{{ news.length }}</span>
    </div>

    <footer v-if="!loading && !error && slides.length > 1 && rotate" class="ticker">
      <!-- The dots say how many screens there are and which one this is; the
           bar says how long is left on it. Together they answer "how long until
           this comes round again", which a bar alone cannot. -->
      <div class="dots">
        <span
          v-for="(s, i) in slides"
          :key="s.key"
          class="dot"
          :class="{ on: i === index }"
        ></span>
      </div>

      <div class="progress">
        <div class="progress-fill" :style="{ width: progress + '%' }"></div>
      </div>

      <span class="next">
        <span class="next-label">Next:</span>
        <span class="next-name">{{ nextSlide.name }}</span>
      </span>
    </footer>

  </div>
</template>


<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { getSiteHierarchy, getPeopleInCountry } from '@/services/GraphService'
import { getOfficeColours, colourFor } from '@/services/OfficeColourService'
import { getStatusTypes, getStatusesToday, statusMeta } from '@/services/StatusService'
import { getActiveNews } from '@/services/NewsService'
import { getDisplayCountries, isEnabled } from '@/services/DisplayService'
import { getDepartmentManagers, managerFor } from '@/services/DepartmentManagerService'
import { getMyDisplayPref } from '@/services/UserPrefService'
import { getCurrentPresence, officeBadge, seenToday } from '@/services/PresenceService'
import { subscribeToPresence } from '@/services/PresenceSocket'
import { statusActiveNow } from '@/services/localTime'
import { getDepartmentLayout, applyLayout } from '@/services/DepartmentLayoutService'
import { role } from '@/auth/authService'

// Where each country's clock comes from. Entra gives an ISO country code and
// nothing else, so the zone has to be mapped.
//
// The US entry is a guess: the country spans six zones, and Entra cannot say
// which one an office is in. The US office is in Texas, so Central.
const COUNTRY_ZONES = {
  DK: 'Europe/Copenhagen',
  SE: 'Europe/Stockholm',
  NO: 'Europe/Oslo',
  DE: 'Europe/Berlin',
  NL: 'Europe/Amsterdam',
  PL: 'Europe/Warsaw',
  GB: 'Europe/London',
  US: 'America/Chicago',   // Texas
  SG: 'Asia/Singapore',
  PK: 'Asia/Karachi',
  IN: 'Asia/Kolkata',
  VN: 'Asia/Ho_Chi_Minh'
}

const ROTATE_MS = 30000
const REFRESH_MS = 5 * 60 * 1000


// News cycles faster than the countries do: an announcement is a sentence, and
// waiting half a minute to see the second one is too long.
const NEWS_ROTATE_MS = 10000

// Departments are packed into columns before anything renders, so the packer
// has to know how tall a box will be. That means the numbers it uses and the
// numbers the CSS uses have to agree.
//
// They previously did not: the type scaled with the viewport while these were
// fixed pixels, so on a large screen the text outgrew the rows the packer
// assumed and boxes overflowed their columns. Everything below is now derived
// from one measured scale, and the same scale is handed to the CSS as custom
// properties.
// Sized so that at scale 1 the type matches what it was before the packing
// rewrite. The heights follow from the name size rather than being chosen
// separately, so making the text bigger cannot silently overflow a box again.
// THE TYPE SIZE. One number.
//
// It used to scale with the screen width, which meant the same board was 14px
// on a laptop and 33px on a monitor, and every attempt to tame it moved a
// different part of the curve. Nobody wanted a curve; they wanted a size.
//
// Change this and everything else follows: row heights, headings, badges and
// column widths are all derived from it, so the packer and the CSS stay in
// agreement.
const NAME_SIZE_PX = 14

const BASE_NAME_SIZE = 18
const BASE_ROW_HEIGHT = 30
const BASE_DEPT_HEADER = 46
const BASE_DEPT_PADDING = 26
const BASE_DEPT_GAP = 18
const BASE_COLUMN_WIDTH = 300

const countries = ref([])
const peopleByCountry = ref({})
const index = ref(0)
const loading = ref(true)
const error = ref(null)
const progress = ref(0)
const clock = ref('')

const news = ref([])
const newsIndex = ref(0)

// Explicit assignments from Admin > Department managers.
const departmentManagers = ref({})

// Kept from the last full refresh so the presence poll can recolour a badge
// without refetching them. They change when an admin edits a location, which is
// rare; presence changes every time somebody opens a door.
const officeColours = ref({})

// The arranged layout, per country. Absent for a country nobody has arranged,
// which is the normal state and means the packer decides.
const departmentLayout = ref({})

// From the person's own settings. False holds one country on screen.
const rotate = ref(true)
const pinnedCountry = ref('')

// Set once after loading, so a pinned country is where the screen opens. Not a
// watcher on every slide change, which would fight the arrows.
let landedOnPin = false

const stage = ref(null)
const stageWidth = ref(0)
const stageHeight = ref(0)

// Read back from what is actually on screen, so the packer works from the real
// layout rather than from constants that drift whenever the styling changes.
const measuredRow = ref(0)
const measuredHeader = ref(0)

/**
 * How much bigger or smaller than the reference layout this screen is.
 *
 * Driven by both dimensions: width alone would make a short, wide screen scale
 * type up until the rows no longer fit vertically. 1400x800 is the reference,
 * and the range is capped so a phone stays legible and a 4K wall does not turn
 * into three enormous names.
 */
const scale = computed(() => {
  // Fixed. The width is still measured -- the packer needs it to decide how
  // many columns fit and when to page -- but it no longer decides how big
  // anything is.
  return NAME_SIZE_PX / BASE_NAME_SIZE
})

// The measurements the packer uses. The CSS reads the same values through the
// custom properties below, so the two cannot drift apart.
const metrics = computed(() => {
  const k = scale.value

  return {
    rowHeight: Math.round(BASE_ROW_HEIGHT * k),
    deptHeader: Math.round(BASE_DEPT_HEADER * k),
    deptPadding: Math.round(BASE_DEPT_PADDING * k),
    deptGap: Math.round(BASE_DEPT_GAP * k),
    columnWidth: Math.round(BASE_COLUMN_WIDTH * k),
    nameSize: Math.round(BASE_NAME_SIZE * k)
  }
})

const layoutVars = computed(() => ({
  '--row-h': metrics.value.rowHeight + 'px',
  '--dept-gap': metrics.value.deptGap + 'px',
  '--name-size': metrics.value.nameSize + 'px',
  '--dept-name-size': Math.round(metrics.value.nameSize * 1.15) + 'px',
  '--badge-size': Math.round(metrics.value.nameSize * 0.75) + 'px',
  '--dept-pad-y': Math.round(metrics.value.deptPadding * 0.42) + 'px',
  '--dept-pad-x': Math.round(metrics.value.deptPadding * 0.62) + 'px'
}))

const columnCount = computed(() =>
  Math.max(1, Math.floor(stageWidth.value / metrics.value.columnWidth))
)


// Badge colours are chosen by hand under Admin > Location and stored in Vero.
// They were previously derived from the office's position in the site list,
// which meant adding an office silently recoloured the others.

/**
 * How a person's row reads.
 *
 * Three states, not two:
 *
 *   in the building   name and badge in full colour
 *   a status set      name in full colour, icon in place of the badge
 *   neither           greyed
 *
 * Somebody working from home is not absent, and they told us where they are,
 * which is more than the people simply not badged in have done. Greying them
 * put them with the unaccounted-for and made the deliberate act of setting a
 * status look like a demotion.
 */
function personClass(person) {
  if (person.checkedIn) return 'is-in'
  if (person.statusIcon) return 'has-status'
  return 'is-out'
}

/**
 * Order one department's people.
 *
 * The manager goes first. Who that is comes from Entra: whoever is named as the
 * manager of someone else in the same department. Deriving it that way rather
 * than from a title means it follows the org chart, and it handles a department
 * with two leads without needing a rule for it.
 *
 * After that, people who are in the building, then alphabetical. Presence loses
 * to the manager position deliberately: the box is a team, and a team reads top
 * down regardless of who happens to be at their desk.
 */
function sortDepartment(people, countryCode, departmentName) {
  // An explicit assignment wins. It says "leads this team", which is the
  // question being asked; Entra's manager field says "reports to", which is a
  // different thing that happens to answer it most of the time.
  const assigned = managerFor(departmentManagers.value, countryCode, departmentName)

  // Fallback: whoever is named as another member's manager in Entra. Used only
  // where nothing has been assigned, so a department nobody has got to still
  // sorts sensibly.
  const derived = assigned
    ? new Set()
    : new Set(people.map((p) => p.managerId).filter(Boolean))

  const leads = (person) => person.id === assigned || derived.has(person.id)

  return [...people].sort((a, b) => {
    if (leads(a) !== leads(b)) return leads(a) ? -1 : 1

    // Nobody is checked in, so the only real distinction is whether someone has
    // set a status. Those go last, since the question the board answers is who
    // might be around.
    if (a.away !== b.away) return a.away ? 1 : -1
    return a.name.localeCompare(b.name)
  })
}

/**
 * Group a country's people by department and pack them into columns.
 *
 * Two things this gets right that the previous version did not.
 *
 * It MEASURES a rendered row rather than deriving its height from a constant.
 * The estimates overshot, so the packer believed a column was full while half
 * of it was visibly empty and people were pushed onto a second page for no
 * reason.
 *
 * And a department LONGER THAN A COLUMN is split across columns instead of
 * being capped. "Unassigned" is routinely forty people, which fits nowhere, and
 * capping it meant one column swallowed the whole page. A continued box repeats
 * the heading so the second half still says which team it is.
 */
function buildPages(people, columns, height, countryCode) {
  const m = metrics.value

  // Measured where possible, estimated only before the first render.
  const rowH = measuredRow.value || m.rowHeight;
  const headerH = measuredHeader.value || (m.deptHeader + m.deptPadding);
  const gap = m.deptGap;

  const byDept = new Map()

  for (const p of people) {
    const dept = p.department || 'Unassigned'
    if (!byDept.has(dept)) byDept.set(dept, [])
    byDept.get(dept).push(p)
  }

  const departments = [...byDept.entries()].map(([name, list]) => ({
    name,
    people: sortDepartment(list, countryCode, name)
  }))

  // An arrangement, if somebody has made one.
  //
  // Then the board is drawn exactly as arranged: boxes stay in the column they
  // were put in, in the order they were put in, with no splitting and no
  // repacking. The point of arranging it is that it stops moving.
  const layout = departmentLayout.value[countryCode]

  if (layout && layout.placements.length > 0) {
    const cols = applyLayout(departments, layout.columns, layout.placements)

    return [cols.map((col) => col.map((d) => ({
      name: d.name,
      people: d.people,
      continued: false,
      key: d.name
    })))]
  }

  // Otherwise pack by size, largest first: big teams settle early and the small
  // ones fill the gaps around them.
  const queue = departments
    .sort((a, b) => b.people.length - a.people.length || a.name.localeCompare(b.name))
    .map((d) => ({ name: d.name, people: [...d.people], offset: 0 }))

  const pages = []

  while (queue.length > 0) {
    const page = Array.from({ length: columns }, () => [])
    let placedOnPage = false

    for (let c = 0; c < columns && queue.length > 0; c++) {
      let remaining = height

      while (queue.length > 0) {
        const dept = queue[0]
        const left = dept.people.length - dept.offset

        const before = page[c].length > 0 ? gap : 0
        const forRows = remaining - before - headerH
        const rowsFit = Math.floor(forRows / rowH)

        // A heading with a single name under it is mostly heading. Unless that
        // is genuinely the whole department, move on rather than leave a stub.
        const minimum = Math.min(2, left)
        if (rowsFit < minimum) break

        const take = Math.min(rowsFit, left)

        page[c].push({
          name: dept.name,
          people: dept.people.slice(dept.offset, dept.offset + take),
          continued: dept.offset > 0,
          key: `${dept.name}#${dept.offset}`
        })

        placedOnPage = true
        remaining -= before + headerH + take * rowH
        dept.offset += take

        if (dept.offset >= dept.people.length) queue.shift()
        else break   // column is full for this department; carry on in the next
      }
    }

    if (!placedOnPage) break   // nothing fits at all; stop rather than loop
    pages.push(page)
  }

  return pages.length > 0 ? pages : [Array.from({ length: columns }, () => [])]
}

/**
 * How many columns to actually draw.
 *
 * An arranged country uses the count it was arranged with. Anything else and
 * the board disagrees with the arrangement: six arranged columns drawn into a
 * grid of five wrap, so the last column's boxes fall under the first and every
 * column comes out wider than intended.
 *
 * Unarranged countries still fit their columns to the screen, which is the
 * right default when nobody has said otherwise.
 */
const gridColumns = computed(() => {
  const layout = departmentLayout.value[current.value.code]
  if (layout && layout.placements.length > 0) return Math.max(1, layout.columns)
  return columnCount.value
})

/**
 * The grid template.
 *
 * On the wall, columns share the width evenly and whatever does not fit is
 * paged. In a window they stop shrinking at a readable width and the stage
 * scrolls: an arrangement made for six columns cannot be squeezed onto a phone,
 * and six columns of truncated names are worse than scrolling to reach them.
 */
const columnTemplate = computed(() => {
  const n = gridColumns.value
  if (kiosk.value) return `repeat(${n}, minmax(0, 1fr))`
  return `repeat(${n}, minmax(190px, 1fr))`
})

const slides = computed(() => {
  const out = []
  const height = stageHeight.value || 800

  for (const country of countries.value) {
    const people = peopleByCountry.value[country.code] ?? []
    if (people.length === 0) continue

    const pages = buildPages(people, columnCount.value, height, country.code)

    pages.forEach((columns, i) => {
      out.push({
        code: country.code,
        name: country.name,
        columns,
        page: i + 1,
        pages: pages.length,
        key: `${country.code}#${i}`
      })
    })
  }

  return out
})

const current = computed(() =>
  slides.value[index.value] ?? { code: '', name: '', columns: [], page: 1, pages: 1 }
)

const currentNews = computed(() => news.value[newsIndex.value] ?? null)

const nextSlide = computed(() =>
  slides.value[(index.value + 1) % Math.max(1, slides.value.length)] ?? current.value
)



const todayLabel = computed(() =>
  new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })
)

let rotateTimer = null
let tickTimer = null
let refreshTimer = null
// Returned by subscribeToPresence. Calling it closes the socket and stops the
// fallback poll.
let stopPresence = null
let newsTimer = null
let hintTimer = null
let observer = null
let cancelled = false

const isFullscreen = ref(false)
const showHint = ref(true)

// The wall screen fills the window; for anyone else the page sits under the
// header and beside the menu, so it takes the space it is given instead.
//
// Fullscreen counts as kiosk too: at that point the browser chrome is gone and
// the app's own should follow.
//
// Declared after isFullscreen deliberately -- a const is not hoisted, so the
// other order throws on load.
const kiosk = computed(() => role() === 'display' || isFullscreen.value)

/**
 * Fullscreen has to be triggered by a user gesture, so this cannot happen on
 * load: the browser rejects a request that did not come from a click or key
 * press. A double-click is that gesture.
 */
async function toggleFullscreen() {
  try {
    if (document.fullscreenElement) {
      await document.exitFullscreen()
    } else {
      await document.documentElement.requestFullscreen()
    }
  } catch (err) {
    // Blocked by policy, or unsupported. Nothing useful to do about it on a
    // screen nobody is watching, so it stays quiet.
    console.warn('Fullscreen request refused', err)
  }
}

// The state is read from the browser rather than tracked ourselves, because
// Escape and F11 also change it and would leave our own flag stale.
function syncFullscreen() {
  isFullscreen.value = document.fullscreenElement !== null
}

function flashHint() {
  showHint.value = true
  clearTimeout(hintTimer)
  hintTimer = setTimeout(() => { showHint.value = false }, 2500)
}

function updateClock() {
  const zone = COUNTRY_ZONES[current.value.code]

  clock.value = new Date().toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    // Falls back to the browser's own zone for a country not in the map, which
    // is wrong but readable, rather than throwing and blanking the clock.
    ...(zone ? { timeZone: zone } : {})
  })
}

function measure() {
  if (!stage.value) return
  stageWidth.value = stage.value.clientWidth
  stageHeight.value = stage.value.clientHeight

  // One rendered row and one rendered box heading. A department name that wraps
  // to two lines is taller, so this takes the largest heading on screen rather
  // than the first: underestimating is what overflows a column.
  const row = stage.value.querySelector('.person')
  if (row) measuredRow.value = row.getBoundingClientRect().height

  const boxes = stage.value.querySelectorAll('.dept')
  let tallest = 0
  for (const box of boxes) {
    const list = box.querySelector('.people')
    const rows = list ? list.getBoundingClientRect().height : 0
    tallest = Math.max(tallest, box.getBoundingClientRect().height - rows)
  }
  if (tallest > 0) measuredHeader.value = tallest
}

/**
 * Update who is checked in, without refetching anybody's details.
 *
 * The directory, colours and statuses are left alone: only the badge state
 * changes between full refreshes, and rebuilding the whole board would make the
 * screen flicker every fifteen seconds.
 */
async function refreshPresence() {
  let presenceMap
  try {
    presenceMap = await getCurrentPresence()
  } catch (err) {
    // A failed poll leaves the last known state on screen rather than clearing
    // it. A momentarily stale board beats one that empties itself.
    console.warn('presence poll failed', err)
    return
  }

  if (cancelled) return

  const next = {}

  for (const [code, people] of Object.entries(peopleByCountry.value)) {
    next[code] = people.map((p) => {
      const raw = presenceMap[p.id] ?? null
      const here = raw && seenToday(raw, COUNTRY_ZONES[code]) ? raw : null

      const office = here?.office_name || null
      const countryCode = here?.country_code || code

      // Re-evaluated every poll: a status scheduled for 13:15 becomes active
      // at 13:15, not at the next five-minute refresh.
      const active = statusActiveNow(p.statusRow, COUNTRY_ZONES[code])
      const meta = active ? statusMeta(p.statusRow.status) : null

      return {
        ...p,
        office,
        badge: office ? officeBadge(office) : null,
        badgeColour: office ? colourFor(officeColours.value, countryCode, office) : null,
        checkedIn: here?.direction === 'ENTERED',
        since: here?.since ?? null,
        statusIcon: meta?.icon ?? null,
        statusLabel: meta?.label ?? null,

        // Recomputed here too. The poll runs every three seconds and this was
        // left at whatever the last full refresh decided, so somebody badging
        // out would get their status icon back while still sorted among the
        // people in the building until the five-minute refresh caught up.
        away: Boolean(meta) && here?.direction !== 'ENTERED'
      }
    })
  }

  peopleByCountry.value = next
}

async function loadAll() {
  try {
    // Colours and statuses are re-read on every refresh, so a change made
    // elsewhere reaches the wall within the refresh interval rather than
    // needing someone to reload it.
    const [result, colourMap, statusMap, newsItems, displaySettings, managerMap, pref, presenceMap] = await Promise.all([
      getSiteHierarchy(),
      getOfficeColours().catch(() => ({})),
      getStatusesToday().catch(() => ({})),
      // News failing should never blank the board, so it degrades to none.
      getActiveNews('display').catch(() => []),
      // Same for the country settings: if they cannot be read, every country is
      // shown rather than none. Failing closed here would leave a blank wall.
      getDisplayCountries().catch(() => ({})),
      getDepartmentManagers().catch(() => ({})),
      // Personal preference. Failing back to the defaults means the dashboard
      // still works for someone whose settings cannot be read, rather than
      // showing nothing.
      getMyDisplayPref().catch(() => ({ rotate: true, pinnedCountry: '' })),
      // Who is actually in a building. Failing back to nobody keeps the board
      // readable rather than blank.
      getCurrentPresence().catch(() => ({})),

      // The status DEFINITIONS, not today's entries.
      //
      // statusMeta reads a cache this call fills. Without it the cache stays on
      // the five seeded defaults, so a status added under Admin > Statuses would
      // appear in the dropdown, be set by somebody, and show no icon at all on
      // the wall -- the one place it matters most.
      //
      // Last in the list deliberately: it is not destructured, and appending
      // rather than inserting means no existing result shifts position.
      // Failing is not fatal; the seeded five still resolve.
      getStatusTypes().catch(() => [])
    ])

    news.value = newsItems
    if (newsIndex.value >= newsItems.length) newsIndex.value = 0

    if (!result || cancelled) return

    // Countries switched off under Admin > Display are skipped entirely: not
    // fetched, not rotated to.
    const shown = result.countries.filter((c) => isEnabled(displaySettings, c.code))

    // Turning cycling off stops the screen advancing on its own. It does NOT
    // narrow the list: every country stays loaded so it can still be reached
    // with the arrows. Cutting the list was the earlier behaviour and it left
    // nothing to navigate to.
    rotate.value = pref.rotate !== false
    pinnedCountry.value = pref.pinnedCountry || ''

    countries.value = shown.map((c) => ({ code: c.code, name: c.name }))

    // One call per country, alongside the people. Failing leaves the order
    // empty for that country, which falls back to packing by size rather than
    // blanking the board.
    const layouts = {}
    for (const country of shown) {
      layouts[country.code] = await getDepartmentLayout(country.code)
        .catch(() => ({ columns: 6, placements: [] }))
    }
    departmentLayout.value = layouts

    departmentManagers.value = managerMap
    officeColours.value = colourMap

    // Open on the pinned country. A pin to something switched off globally, or
    // to a country that no longer exists, simply does not match and the screen
    // starts at the beginning rather than going blank.
    if (!landedOnPin && pinnedCountry.value) {
      await nextTick()
      const at = slides.value.findIndex((sl) => sl.code === pinnedCountry.value)
      if (at >= 0) index.value = at
      landedOnPin = true
    }

    // Everything is fetched up front rather than on rotation, so a country
    // change is instant. The directory is cached server-side, so these are
    // cheap calls.
    for (const country of shown) {
      const merged = []

      // One call per country rather than one per office.
      //
      // Asking per office matched on officeLocation, so anybody with a blank
      // one was never fetched at all. That field is sparsely filled, so most of
      // the directory was missing from the board and nothing said so. The
      // office is a decoration on a person, not a condition of their existence.
      const people = await getPeopleInCountry(country.code)
      if (cancelled) return

      for (const p of people ?? []) {
        const status = statusMap[p.id]

        // Only a status IN FORCE right now. One scheduled for this afternoon
        // is stored today but has not started, and the board was showing it
        // the moment it was saved.
        const active = statusActiveNow(status, COUNTRY_ZONES[country.code])
        const meta = active ? statusMeta(status.status) : null

        // Where the door says they are, if it says anything.
        // Keyed on the object id now that the badge mapping resolves to one.
        // A badge event from today, in this country's own day. Yesterday's
        // event is not today's answer: somebody who badged in on Monday and
        // never badged out is not still in the building on Tuesday morning.
        const raw = presenceMap[p.id] ?? null
        const here = raw && seenToday(raw, COUNTRY_ZONES[country.code]) ? raw : null

        // The door, and only the door.
        //
        // This used to fall back to the person's Entra officeLocation when they
        // were not checked in. Locations are Vero's own now, so that fallback
        // drew a badge from a field nobody configures here -- and since the
        // field is set on only a handful of accounts, it made those few people
        // look different from their colleagues for no reason anyone could see.
        //
        // The badge now means one thing: a door said you are here.
        const office = here?.office_name || null
        const countryCode = here?.country_code || country.code

        merged.push({
          ...p,
          office,
          // No office means no badge, rather than a placeholder standing in for
          // one. A blank field should read as blank.
          badge: office ? officeBadge(office) : null,
          badgeColour: office ? colourFor(colourMap, countryCode, office) : null,
          statusIcon: meta?.icon ?? null,
          statusLabel: meta?.label ?? null,
          // The raw row, so the poll can notice a status starting or ending
          // between full refreshes rather than waiting up to five minutes.
          statusRow: status ?? null,

          // Absent from the presence list means not inside.
          // Here today AND the last thing they did was come in. Someone who
          // has left keeps their badge for the rest of the day, greyed: they
          // were here, which is a different fact from never having been.
          checkedIn: here?.direction === 'ENTERED',
          since: here?.since ?? null,

          // Away means: has a status set AND is not currently in a building.
          //
          // Not "&& !here": after they badge OUT, here is still today's event,
          // so that would have kept them sorted with the present all afternoon.
          // A checkout returns them to their status, which is the point -- the
          // trip is still on, they have just left the office.
          away: Boolean(meta) && here?.direction !== 'ENTERED'
        })
      }

      peopleByCountry.value = { ...peopleByCountry.value, [country.code]: merged }
    }

    error.value = null
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  } finally {
    if (!cancelled) {
      loading.value = false
      await nextTick()
      measure()
    }
  }
}

function advance() {
  if (slides.value.length === 0) return
  index.value = (index.value + 1) % slides.value.length
  progress.value = 0
}

// The timer only advances when cycling is on. The arrows call advance directly,
// so manual movement still works with it off.
function tick() {
  if (!rotate.value) return
  advance()
}

function back() {
  if (slides.value.length === 0) return
  index.value = (index.value - 1 + slides.value.length) % slides.value.length
  progress.value = 0
}

// Shown only when cycling is off and there is somewhere to go. On the wall
// screen nobody is standing there to press them.
const showArrows = computed(() => !rotate.value && !kiosk.value && slides.value.length > 1)

// Resizing repacks the columns, which changes the page count underneath us.
watch(slides, (list) => {
  if (index.value >= list.length) index.value = 0
})

// Rotating to another country changes the zone, so the clock is recomputed
// immediately rather than waiting up to a quarter second for the next tick.
watch(() => current.value.code, updateClock)

// Remeasure once the new page has rendered: a wrapped department name on this
// page changes the header height the packer should be using.
watch(() => current.value.key, async () => {
  await nextTick()
  measure()
})

onMounted(async () => {
  updateClock()
  await nextTick()
  measure()

  observer = new ResizeObserver(measure)

  // Observe the stage WHENEVER IT APPEARS, not only at mount.
  //
  // The stage is behind a v-if: it does not exist until the directory has
  // loaded and there is something to draw. At mount the ref is still null, so
  // observe() was called on nothing and the element was never watched. Resizes
  // after that -- opening a sidebar, opening devtools, moving to another window
  // -- went unnoticed, and the board kept drawing at whatever width it happened
  // to measure first. That is why the type changed size when you navigated away
  // and came back: same window, stale measurement.
  watch(stage, (el) => {
    if (!observer) return
    observer.disconnect()
    if (el) {
      observer.observe(el)
      measure()
    }
  }, { immediate: true })

  await loadAll()

  // Once more after the first load, when the boxes exist and a row can actually
  // be measured.
  await nextTick()
  measure()

  rotateTimer = setInterval(tick, ROTATE_MS)

  tickTimer = setInterval(() => {
    progress.value = Math.min(100, progress.value + (100 / (ROTATE_MS / 250)))
    updateClock()
  }, 250)

  refreshTimer = setInterval(loadAll, REFRESH_MS)

  // The frequent one. Separate from the full refresh so a badge appears within
  // seconds without asking Microsoft Graph for the directory every time.
  // A subscription rather than an interval.
  //
  // It opens a socket and calls back when a door reports something, and keeps
  // polling underneath: slowly while the socket is healthy, at the old rate
  // whenever it is not. A wall screen runs unattended for weeks, and a socket
  // that dies quietly would leave the board frozen showing yesterday -- which
  // is worse than a slow board, because it looks fine.
  stopPresence = subscribeToPresence(refreshPresence)

  document.addEventListener('fullscreenchange', syncFullscreen)
  window.addEventListener('mousemove', flashHint)
  flashHint()

  newsTimer = setInterval(() => {
    if (news.value.length > 1) {
      newsIndex.value = (newsIndex.value + 1) % news.value.length
    }
  }, NEWS_ROTATE_MS)
})

onBeforeUnmount(() => {
  cancelled = true
  clearInterval(rotateTimer)
  clearInterval(tickTimer)
  clearInterval(refreshTimer)
  if (stopPresence) stopPresence()
  clearInterval(newsTimer)
  clearTimeout(hintTimer)
  document.removeEventListener('fullscreenchange', syncFullscreen)
  window.removeEventListener('mousemove', flashHint)
  if (observer) observer.disconnect()
})
</script>


<style scoped>
.hint {
  position: fixed;
  bottom: clamp(56px, 6vh, 90px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: #ffffff;
  background: rgba(31, 56, 100, 0.82);
  padding: 7px 14px;
  border-radius: 20px;
  pointer-events: none;
}

.hint-fade-enter-active,
.hint-fade-leave-active { transition: opacity 0.4s ease; }

.hint-fade-enter-from,
.hint-fade-leave-to { opacity: 0; }

.display {
  --ink: #1f3864;
  --ink-soft: #7d8794;
  --accent: #ed6e01;
  --line: #c9d2df;
  --paper: #ffffff;

  /* Fills the space it is given, under the header and beside the menu. It used
     to be fixed to the viewport, which meant it ignored both and slid under
     them. */
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px);

  background: var(--paper);
  color: var(--ink);
  overflow: hidden;
  /* A double-click would otherwise select the nearest text. */
  user-select: none;
}

/* The wall screen, or anyone who has gone fullscreen: take the whole window
   and sit above everything else. */
.display.kiosk {
  position: fixed;
  inset: 0;
  height: auto;
  z-index: 200;
}

/* ---------- Bar ---------- */

.bar {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: clamp(12px, 1.2vw, 22px) clamp(22px, 2.6vw, 52px);
  border-bottom: 1px solid var(--line);
}

/* Pushed clear of the back button when it is present. */

.brand {
  display: flex;
  align-items: center;
  gap: clamp(10px, 1vw, 18px);
  min-width: 0;
}

.logo { height: clamp(20px, 1.8vw, 34px); width: auto; display: block; }

.divider { width: 1px; height: clamp(16px, 1.4vw, 26px); background: var(--line); }

.product {
  font-size: clamp(15px, 1.2vw, 24px);
  font-weight: 700;
  color: var(--ink);
}

/* Centred by giving the brand and clock equal flex, rather than absolute
   positioning, so a long country name pushes both sides evenly instead of
   overlapping one. */




.now {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: clamp(10px, 1vw, 20px);
  flex: 1;
  min-width: 0;
}


.clock {
  font-size: clamp(22px, 2.2vw, 42px);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}

.today {
  font-size: clamp(12px, 1vw, 18px);
  color: var(--ink-soft);
  white-space: nowrap;
}

.next {
  font-size: clamp(11px, 0.9vw, 17px);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-soft);
  white-space: nowrap;
}

/* ---------- Departments ---------- */

/* The country sits on its own line above the departments, as a chip and a
   heading, rather than as one of a row of tabs. It is the only thing on that
   line, so it can be large enough to read from across a corridor. */
.site-head {
  flex: none;
  display: flex;
  align-items: center;
  gap: clamp(10px, 0.9vw, 16px);
  padding: clamp(12px, 1.3vw, 24px) clamp(22px, 2.6vw, 52px) clamp(8px, 0.8vw, 14px);
}

.chip {
  flex: none;
  font-size: clamp(11px, 0.85vw, 17px);
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--paper);
  background: #1f3864;
  padding: 0.42em 0.7em;
  border-radius: 6px;
}

.site-name {
  font-size: clamp(22px, 2.1vw, 38px);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  margin: 0;
  color: #1f3864;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.inline-now {
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
  margin-left: 18px;
}

.inline-clock {
  font-size: clamp(16px, 1.4vw, 24px);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}

.inline-today {
  font-size: clamp(12px, 0.95vw, 16px);
  color: var(--ink-soft);
}

.steps {
  display: flex;
  gap: 8px;
  margin-left: auto;
  flex: none;
}

.step {
  font-family: inherit;
  font-size: clamp(14px, 1.1vw, 20px);
  line-height: 1;
  width: clamp(34px, 2.6vw, 46px);
  height: clamp(34px, 2.6vw, 46px);
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--paper);
  color: #1f3864;
  cursor: pointer;
}

.step:hover {
  background: #1f3864;
  border-color: #1f3864;
  color: var(--paper);
}

.page-of {
  flex: none;
  font-size: clamp(11px, 0.9vw, 17px);
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--ink-soft);
  background: #eef1f4;
  padding: 0.35em 0.7em;
  border-radius: 6px;
  font-variant-numeric: tabular-nums;
}

.stage {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: clamp(12px, 1.4vw, 26px) clamp(20px, 2.4vw, 48px);
}

/* On a wall screen, hidden is right: nobody is standing there to scroll, and
   anything cut off is simply not shown, which is what the paging is for.
   In a window there IS somebody, and a fixed arrangement on a laptop or a phone
   will not fit however it is drawn. Better to let them reach it. */
.display:not(.kiosk) .stage {
  overflow: auto;
}

/* Columns stop shrinking at a readable width and the stage scrolls sideways
   instead. Six arranged columns squeezed into a phone are six columns of
   truncated initials, which is worse than scrolling to them. */
.display:not(.kiosk) .columns {
  height: auto;
  min-height: 100%;
}

.columns {
  display: grid;
  gap: clamp(12px, 1.2vw, 22px);
  height: 100%;
  align-items: start;
}

.column {
  display: flex;
  flex-direction: column;
  gap: var(--dept-gap, 16px);
  min-width: 0;
}

.dept {
  border: 2px solid #2f4f7f;
  padding: var(--dept-pad-y, 8px) var(--dept-pad-x, 12px);
}

.dept-name {
  font-size: var(--dept-name-size, 16px);
  font-weight: 700;
  text-align: center;
  margin: 0 0 6px;
  padding-bottom: 5px;
  border-bottom: 2px solid #2f4f7f;
  color: var(--ink);
}

/* A department too long for one column continues in the next. The marker keeps
   the repeated heading from reading as a second team of the same name. */
.cont {
  font-size: 0.7em;
  font-weight: 400;
  letter-spacing: 0.04em;
  color: var(--ink-soft);
  margin-left: 0.5em;
}

.people {
  list-style: none;
  margin: 0;
  padding: 0;
}

/* Fixed height, matching the packer's row height exactly. Letting it grow with
   its content is what allowed a box to be taller than the space reserved for
   it. */
.person {
  display: flex;
  align-items: center;
  gap: 8px;
  height: var(--row-h, 26px);
  min-width: 0;
}

/* One slot width for all three.
   
   They were each 1.9em, and em scales with the element's OWN font size: the
   badge at 11px, the icon at 12px, and the empty gap inheriting the name's
   14px. Three different widths, so a name with a badge started in a different
   place from a name without one and the column never lined up.
   
   Calculated from --badge-size so the slot still scales with the board. */
.badge {
  flex: none;
  width: calc(var(--badge-size, 12px) * 1.9);
  font-size: var(--badge-size, 11px);
  font-weight: 700;
  line-height: 1.5;
  text-align: center;
  color: var(--paper);
  background: var(--accent);
  border-radius: 3px;
}

/* Sized and spaced to occupy the same slot as the office badge, so a row with
   a status does not shift relative to the ones around it. */
.status-icon {
  flex: none;
  width: calc(var(--badge-size, 12px) * 1.9);
  font-size: var(--badge-size, 12px);
  line-height: 1.5;
  text-align: center;
}

.badge-gap {
  flex: none;
  width: calc(var(--badge-size, 12px) * 1.9);
}

.person-name {
  font-size: var(--name-size, 15px);
  font-weight: 500;
  line-height: 1.5;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Anyone neither in the building nor with a status set is greyed: the badge
   loses its colour and the name recedes, so the screen reads as "who is here"
   at a glance.
   
   Somebody with a status is deliberately NOT in this rule. Their name stays at
   full strength and the icon says where they are; they are accounted for, which
   is the distinction the greying is meant to draw. */
.person.is-out .badge {
  background: #c4ccd6;
  color: #ffffff;
}

.person.is-out .person-name { color: #9aa5b2; }

/* ---------- Ticker ---------- */

.news-strip {
  flex: none;
  display: flex;
  align-items: center;
  gap: clamp(12px, 1.2vw, 22px);
  padding: clamp(10px, 1vw, 18px) clamp(20px, 2.4vw, 48px);
  background: #eef4f3;
  border-top: 1px solid #b9d2cf;
}

.news-mark {
  flex: none;
  font-size: clamp(10px, 0.75vw, 14px);
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--paper);
  background: #1f3864;
  padding: 0.35em 0.7em;
  border-radius: 4px;
}

.news-body {
  flex: 1;
  margin: 0;
  font-size: clamp(14px, 1.15vw, 24px);
  line-height: 1.35;
  color: #1f3864;
  /* Two lines at most: past that nobody reads it from a corridor anyway, and
     the people below start losing room. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-count {
  flex: none;
  font-size: clamp(11px, 0.85vw, 16px);
  font-weight: 700;
  color: #6f948f;
  font-variant-numeric: tabular-nums;
}

.ticker {
  flex: none;
  display: flex;
  align-items: center;
  gap: clamp(14px, 1.6vw, 30px);
  padding: clamp(12px, 1.1vw, 22px) clamp(22px, 2.6vw, 52px);
}

.dots {
  display: flex;
  gap: 7px;
  flex: none;
}

.dot {
  width: clamp(7px, 0.55vw, 11px);
  height: clamp(7px, 0.55vw, 11px);
  border-radius: 50%;
  background: #d6dce4;
}

.dot.on { background: var(--accent); }

/* A hairline rule the whole width, filled from the left as the screen's time
   runs down. */
.progress {
  flex: 1;
  height: 3px;
  border-radius: 2px;
  background: #e7ebf0;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--accent);
  transition: width 0.25s linear;
}

.next {
  flex: none;
  display: inline-flex;
  align-items: baseline;
  gap: 9px;
}

.next-label {
  font-size: clamp(10px, 0.8vw, 15px);
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.next-name {
  font-size: clamp(14px, 1.15vw, 24px);
  font-weight: 700;
  color: #1f3864;
}

/* ---------- States ---------- */

.state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(15px, 1.3vw, 24px);
  color: var(--ink-soft);
}

.state.error { color: #b3261e; }

@media (prefers-reduced-motion: reduce) {
  .progress-fill { transition: none; }
  .country { transition: none; }
}
</style>
